import React, { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import { AdminOrServiceRoute, AdminRoute, DashboardRoute, SoftwareUsageRoute } from './components/ProtectedRoute';
import { getDefaultHomePath } from './utils/permissions';
import Layout from './views/Layout';

const Login = lazy(() => import('./views/Login'));
const Dashboard = lazy(() => import('./views/Dashboard'));
const OrderFill = lazy(() => import('./views/OrderFill'));
const OrderPlace = lazy(() => import('./views/OrderPlace'));
const QueryInfo = lazy(() => import('./views/QueryInfo'));
const Recruitment = lazy(() => import('./views/Recruitment'));
const Stores = lazy(() => import('./views/Stores'));
const HealthCertificates = lazy(() => import('./views/HealthCertificates'));
const Blacklist = lazy(() => import('./views/Blacklist'));
const Users = lazy(() => import('./views/Users'));
const Banners = lazy(() => import('./views/Banners'));
const SoftwareUsage = lazy(() => import('./views/SoftwareUsage'));

const preloadRouteChunks = [
  () => import('./views/Login'),
  () => import('./views/Dashboard'),
  () => import('./views/OrderFill'),
  () => import('./views/OrderPlace'),
  () => import('./views/QueryInfo'),
  () => import('./views/Recruitment'),
  () => import('./views/Stores'),
  () => import('./views/HealthCertificates'),
  () => import('./views/Blacklist'),
  () => import('./views/Users'),
  () => import('./views/Banners'),
  () => import('./views/SoftwareUsage'),
];

const HomeRedirect = () => {
  const { user } = useAuth();
  return <Navigate to={getDefaultHomePath(user)} replace />;
};

const RouteLoading = () => null;

const PageSuspense = ({ children }) => (
  <Suspense fallback={<RouteLoading />}>
    {children}
  </Suspense>
);

function App() {
  useEffect(() => {
    let cancelled = false;
    let idleId = null;
    let timerId = null;

    const preload = () => {
      if (cancelled) return;
      preloadRouteChunks.forEach((load) => {
        load().catch(() => {});
      });
    };

    if ('requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(preload, { timeout: 2500 });
    } else {
      timerId = window.setTimeout(preload, 900);
    }

    return () => {
      cancelled = true;
      if (idleId !== null && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleId);
      }
      if (timerId !== null) {
        window.clearTimeout(timerId);
      }
    };
  }, []);

  return (
    <AuthProvider>
      <div className="App">
        <Routes>
          <Route path="/login" element={
            <PageSuspense>
              <Login />
            </PageSuspense>
          } />
          <Route path="/" element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }>
            <Route index element={<HomeRedirect />} />
            <Route path="dashboard" element={
              <DashboardRoute>
                <PageSuspense>
                  <Dashboard />
                </PageSuspense>
              </DashboardRoute>
            } />
            <Route path="order-fill" element={
              <PageSuspense>
                <OrderFill />
              </PageSuspense>
            } />
            <Route path="order-place" element={
              <PageSuspense>
                <OrderPlace />
              </PageSuspense>
            } />
            <Route path="query-info" element={
              <PageSuspense>
                <QueryInfo />
              </PageSuspense>
            } />
            <Route path="recruitment" element={
              <PageSuspense>
                <Recruitment />
              </PageSuspense>
            } />
            <Route path="stores" element={
              <PageSuspense>
                <Stores />
              </PageSuspense>
            } />
            <Route path="health-certificates" element={
              <PageSuspense>
                <HealthCertificates />
              </PageSuspense>
            } />
            <Route path="blacklist" element={
              <PageSuspense>
                <Blacklist />
              </PageSuspense>
            } />
            <Route path="banners" element={
              <AdminOrServiceRoute>
                <PageSuspense>
                  <Banners />
                </PageSuspense>
              </AdminOrServiceRoute>
            } />
            <Route path="users" element={
              <PageSuspense>
                <Users />
              </PageSuspense>
            } />
            <Route path="software-usage" element={
              <SoftwareUsageRoute>
                <PageSuspense>
                  <SoftwareUsage />
                </PageSuspense>
              </SoftwareUsageRoute>
            } />
          </Route>
        </Routes>
      </div>
    </AuthProvider>
  );
}

export default App;
